import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { DataResult, DataSourceRequestState } from '@progress/kendo-data-query';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionButton,
  DeleteButton
} from 'src/components/shared/Button/Button';
import { LoadingPanel } from 'src/components/shared/DataGrid/LoadingPanel';
import { Icon } from 'src/components/shared/Icon/Icon';
import { StatusCircle } from 'src/components/shared/StatusCircle/StatusCircle';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { LabelType } from 'src/components/shared/Typography/LabelType';
import { ProductDto, ProductStatus } from 'src/store/products/product';
import { formatDate } from 'src/utils/date';
import styled from 'styled-components';
import { productStatusColorMap } from './Product/ProductDetailsForm';

type Props = {
  data: DataResult;
  loading: boolean;
  requestState: DataSourceRequestState;
  onEdit: (product: ProductDto) => void;
  onDelete: (product: ProductDto) => void;
  onPageChange: (skip: number, take: number) => void;
};

export const ProductsListMobile: React.FC<Props> = ({
  data,
  loading,
  requestState,
  onEdit,
  onDelete,
  onPageChange
}) => {
  const { t } = useTranslation();

  return (
    <Fragment>
      <Pagination
        count={Math.ceil(data.total / (requestState.take ?? 20))}
        siblingCount={0}
        onChange={(_, page: number) => {
          const take = requestState.take ?? 20;
          onPageChange(page * take, take);
        }}
        variant="outlined"
        color="primary"
        className="mb-24"
      />

      {loading && <LoadingPanel />}

      {data.data.map((product: ProductDto) => (
        <Accordion key={product.id}>
          <AccordionSummary expandIcon={<Icon type="la-angle-down" />}>
            <Box display="flex" alignItems="center" width="100%">
              <Grid container spacing={0}>
                <Grid item xs={5}>
                  <LabelType>{t('product.runNo')}</LabelType>
                  <BodyType>{product.runNo}</BodyType>
                </Grid>
                <Grid item xs={7}>
                  <LabelType>{t('runSystem.singular')}</LabelType>
                  <BodyType>{product.runSystemName}</BodyType>
                </Grid>
              </Grid>
              <StatusCircle
                color={
                  productStatusColorMap[
                    product.productStatusId as ProductStatus
                  ]
                }
                className="ml-auto"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails style={{ display: 'block' }} className="pr-64">
            <Grid container spacing={0}>
              <Grid item xs={5}>
                <DetailsList>
                  <li>
                    <LabelType>{t('product.runText')}</LabelType>
                    <BodyType>{product.runText ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('product.lotText')}</LabelType>
                    <BodyType>{product.lotText ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('product.runDate')}</LabelType>
                    <BodyType>{formatDate(product.runDate)}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('product.lotDate')}</LabelType>
                    <BodyType>{formatDate(product.lotDate) ?? '–'}</BodyType>
                  </li>
                </DetailsList>
              </Grid>

              <Grid item xs={7}>
                <DetailsList>
                  <li>
                    <LabelType>{t('product.lotNo')}</LabelType>
                    <BodyType>{product.lotNo ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('grade.singular')}</LabelType>
                    <BodyType>{product.gradeName ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('productType.singular')}</LabelType>
                    <BodyType>{product.productTypeName ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('segment.singular')}</LabelType>
                    <BodyType>{product.segmentName ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('specification.pspNo')}</LabelType>
                    <BodyType>{product.specificationNo ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('specification.psp')}</LabelType>
                    <BodyType>{product.specificationName ?? '–'}</BodyType>
                  </li>
                </DetailsList>
              </Grid>
            </Grid>

            <hr />

            <Box display="flex">
              <ActionButton
                icon="la la-pen"
                onClick={() => onEdit(product)}
                className="mr-16"
              />

              <DeleteButton onClick={() => onDelete(product)} />
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Fragment>
  );
};

export const DetailsList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;

  li {
    padding: 0.25rem 0;
  }
`;
